import { useEffect, useState, type RefObject } from 'react';
import { attachGuestVideoSurface } from '../../api/modules/peer';
import { CanvasPresentationTarget, type VideoPresentationMode } from './CanvasPresentationTarget';

/**
 * Binds a WebCodecs canvas surface to the player's `<video>` for the life of the component
 * and reports which surface currently owns video. The returned mode is what lets the
 * player's conventional-video watchdogs stand down: with WebCodecs selected, the `<video>`
 * legitimately stops advancing, and a stall escalation would tear down a healthy session.
 */
export function useWebCodecsSurface(videoRef: RefObject<HTMLVideoElement | null>): VideoPresentationMode {
  const [mode, setMode] = useState<VideoPresentationMode>('stream');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const target = new CanvasPresentationTarget({ video, onModeChange: setMode });
    const detach = attachGuestVideoSurface(target);
    return () => {
      detach();
      target.releaseCanvas();
      setMode('stream');
    };
    // The video element is mounted once per player; a callback ref assigns it before effects run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return mode;
}
